import React, { useState, useEffect, useRef } from 'react'
import { Search, ChevronDown, User, Building } from 'lucide-react'

interface Cliente {
  id: string
  nome_completo: string
  nome_empresa?: string
  email?: string
}

interface ClientSearchableDropdownProps {
  clientes: Cliente[]
  selectedClientId: string
  onSelect: (clienteId: string) => void
  placeholder?: string
  disabled?: boolean
  required?: boolean
}

const ClientSearchableDropdown: React.FC<ClientSearchableDropdownProps> = ({
  clientes,
  selectedClientId,
  onSelect,
  placeholder = "Selecione um cliente",
  disabled = false,
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Cliente selecionado
  const selectedClient = clientes.find(cliente => cliente.id === selectedClientId)

  // Filtrar clientes baseado no termo de busca
  const filteredClientes = clientes.filter(cliente => {
    const searchLower = searchTerm.toLowerCase()
    const nomeMatch = cliente.nome_completo.toLowerCase().includes(searchLower)
    const empresaMatch = cliente.nome_empresa?.toLowerCase().includes(searchLower) || false
    const emailMatch = cliente.email?.toLowerCase().includes(searchLower) || false
    
    return nomeMatch || empresaMatch || emailMatch
  }).slice(0, 10) // Limitar a 10 resultados para performance

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
        setHighlightedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focar no input quando abrir
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Navegação por teclado
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => 
          prev < filteredClientes.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredClientes.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && filteredClientes[highlightedIndex]) {
          handleSelectClient(filteredClientes[highlightedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setSearchTerm('')
        setHighlightedIndex(-1)
        break
    }
  }

  const handleSelectClient = (cliente: Cliente) => {
    onSelect(cliente.id)
    setIsOpen(false)
    setSearchTerm('')
    setHighlightedIndex(-1)
  }

  const handleToggleDropdown = () => {
    if (disabled) return
    setIsOpen(!isOpen)
    if (!isOpen) {
      setSearchTerm('')
      setHighlightedIndex(-1)
    }
  }

  const highlightSearchTerm = (text: string, term: string) => {
    if (!term) return text
    
    const regex = new RegExp(`(${term})`, 'gi')
    const parts = text.split(regex)
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 font-medium">
          {part}
        </span>
      ) : part
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggleDropdown}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:bg-gray-50'}
          ${isOpen ? 'ring-2 ring-orange-500 border-transparent' : 'border-gray-300'}
          ${!selectedClient ? 'text-gray-500' : 'text-gray-900'}
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {selectedClient ? (
              <>
                <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="truncate">
                  <span className="font-medium">{selectedClient.nome_completo}</span>
                  {selectedClient.nome_empresa && (
                    <span className="text-gray-500 ml-1">- {selectedClient.nome_empresa}</span>
                  )}
                </div>
              </>
            ) : (
              <>
                <User className="w-4 h-4 text-gray-400" />
                <span>{placeholder}</span>
                {required && <span className="text-red-500 ml-1">*</span>}
              </>
            )}
          </div>
          <ChevronDown 
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setHighlightedIndex(-1)
                }}
                onKeyDown={handleKeyDown}
                placeholder="Buscar cliente..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Results */}
          <div className="max-h-60 overflow-y-auto">
            {filteredClientes.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {searchTerm ? 'Nenhum cliente encontrado' : 'Digite para buscar clientes'}
              </div>
            ) : (
              filteredClientes.map((cliente, index) => (
                <button
                  key={cliente.id}
                  type="button"
                  onClick={() => handleSelectClient(cliente)}
                  className={`
                    w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                    ${index === highlightedIndex ? 'bg-gray-50' : ''}
                    ${selectedClientId === cliente.id ? 'bg-orange-50 text-orange-900' : ''}
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {cliente.nome_empresa ? (
                        <Building className="w-4 h-4 text-gray-400" />
                      ) : (
                        <User className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {highlightSearchTerm(cliente.nome_completo, searchTerm)}
                      </div>
                      {cliente.nome_empresa && (
                        <div className="text-sm text-gray-500 truncate">
                          {highlightSearchTerm(cliente.nome_empresa, searchTerm)}
                        </div>
                      )}
                      {cliente.email && (
                        <div className="text-xs text-gray-400 truncate">
                          {highlightSearchTerm(cliente.email, searchTerm)}
                        </div>
                      )}
                    </div>
                    {selectedClientId === cliente.id && (
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer com contador */}
          {filteredClientes.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200 text-xs text-gray-500 bg-gray-50">
              {filteredClientes.length} de {clientes.length} clientes
              {filteredClientes.length === 10 && clientes.length > 10 && ' (mostrando primeiros 10)'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ClientSearchableDropdown
